import { UserClaims } from '@okta/okta-auth-js';

import { oktaAuth } from '..';
import { UserLoginModel } from '../types/lib/userLoginModel';
import { ToolBoxService } from './ToolBoxService';

const keys = {
  oktaCache: 'okta-cache-storage',
  oktaToken: 'okta-token-storage',
  user: 'current-user',
  userSettings: 'current-user-settings',
  brandCode: 'current-client',
  clientId: 'current-clientId',
  redirect: 'redirect-uri',
  lastClient: 'last-login-client',
};

export class AuthService {
  static hasAnyLoginSessionData() {
    return (
      this.getCurrentClientId() > 0 ||
      ToolBoxService.hasValue(window.localStorage.getItem(keys.user)) ||
      ToolBoxService.hasValue(window.localStorage.getItem(keys.brandCode)) ||
      ToolBoxService.hasValue(window.localStorage.getItem(keys.oktaCache)) ||
      ToolBoxService.hasValue(window.localStorage.getItem(keys.oktaToken))
    );
  }

  static logout = async () => {
    try {
      oktaAuth
        .signOut({
          postLogoutRedirectUri: window.location.origin + '/login', // Must be registered in Okta
        })
        .catch((message) => {
          console.error('Error signing out:', message);
        })
        .finally(async () => {
          await this.clearOktaTokens();
        });
    } catch {
      console.error('Error');
    }
  };
  static getAccessToken = async () => {
    const tokens = await oktaAuth.tokenManager.getTokens();
    return tokens.accessToken?.accessToken || null;
  };
  // Removes the okta tokens from local storage.
  static async clearOktaTokens() {
    oktaAuth.clearStorage();
    await oktaAuth.closeSession();
    window.localStorage.removeItem(keys.oktaToken);
    window.localStorage.removeItem(keys.oktaCache);
  }

  // Check that Okta access Token exists and is not expired.
  //  This -Does Not- check if it's Okta-approved valid so should not be used for access auth.
  static oktaAccessTokenNotExpired() {
    const tokens = ToolBoxService.parseJson(window.localStorage.getItem(keys.oktaToken) ?? '{}');
    const token = tokens?.accessToken;
    const parsedJwt = ToolBoxService.parseJwt(token?.accessToken);
    const expiresAt = (token?.expiresAt ?? 0) * 1000;
    const expiresAt2 = (parsedJwt?.exp ?? 0) * 1000;
    if (Date.now() > expiresAt || Date.now() > expiresAt2) {
      return false;
    }
    return true;
  }

  // Gathers the current user from local storage IF Okta access token is not expired
  static getCurrentUser() {
    const localStorageUser = window.localStorage.getItem(keys.user) ?? '{}';
    const user = (ToolBoxService.parseJson(localStorageUser) ?? {}) as unknown as UserClaims;
    if (this.oktaAccessTokenNotExpired()) {
      return user;
    }
    return {} as UserClaims;
  }
  // Sets the current user in local storage.
  static setCurrentUser(value: UserClaims | string) {
    window.localStorage.setItem(keys.user, JSON.stringify(value));
  }
  // Removes the current user from local storage.
  static clearCurrentUser() {
    window.localStorage.removeItem(keys.user);
  }

  // Gathers the last login client from local storage.
  static getLastLoginClient() {
    return window.localStorage.getItem(keys.lastClient) || null;
  }
  // Sets the last login client in local storage. Remove entry if empty
  static setLastLoginClient(value: string | null) {
    if (!value) {
      this.clearLastLoginClient();
    } else {
      window.localStorage.setItem(keys.lastClient, value.toUpperCase());
    }
  }
  // Removes the last login client from local storage.
  static clearLastLoginClient() {
    window.localStorage.removeItem(keys.lastClient);
  }

  // Gathers the current client from local storage.
  static getCurrentBrandCode() {
    return window.localStorage.getItem(keys.brandCode);
  }
  // Sets the current client in local storage. Remove entry if empty
  static setCurrentBrandCode(value: string | null) {
    if (!value) {
      this.clearCurrentBrandCode();
    } else {
      window.localStorage.setItem(keys.brandCode, value.toUpperCase());
    }
  }
  // Removes the current client from local storage.
  static clearCurrentBrandCode() {
    window.localStorage.removeItem(keys.brandCode);
  }

  //Gather the Current ClientId
  static getCurrentClientId() {
    const value = window.localStorage.getItem(keys.clientId) || '0';
    return !isNaN(Number(value)) ? parseInt(value) : 0;
  }
  // Sets the current clientId in local storage. Remove entry if empty
  static setCurrentClientId(value: number | null) {
    if (!value) {
      this.clearCurrentClientId();
    } else {
      window.localStorage.setItem(keys.clientId, value.toString());
    }
  }
  // Removes the current clientId from local storage.
  static clearCurrentClientId() {
    window.localStorage.removeItem(keys.clientId);
  }

  //Gather the Current User Settings
  static getCurrentUserSettings() {
    const value = window.localStorage.getItem(keys.userSettings) ?? '';
    const json = ToolBoxService.parseJson(value);
    return !json ? null : (json as unknown as UserLoginModel);
  }
  // Sets the current User Settings
  static setCurrentUserSettings(value: UserLoginModel) {
    window.localStorage.setItem(keys.userSettings, JSON.stringify(value));
  }
  // Removes the current User Settings
  static clearCurrentUserSettings() {
    window.localStorage.removeItem(keys.userSettings);
  }

  // Get the Redirect Uri
  static getRedirectUri() {
    return window.localStorage.getItem(keys.redirect) || '';
  }
  // Sets the Redirect Uri in local storage. Remove entry if empty
  static setRedirectUri(value: string) {
    if (!value || value === '') {
      this.clearRedirectUri();
    } else {
      window.localStorage.setItem(keys.redirect, value.toString());
    }
  }
  // Removes the Redirect Uri from local storage.
  static clearRedirectUri() {
    window.localStorage.removeItem(keys.redirect);
  }
}
